import React, { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch('https://formspree.io/f/xvgakzkd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-gray-50" aria-labelledby="contact-title">
      <div className="container mx-auto px-4 sm:px-6">
        <h2 id="contact-title" className="text-2xl sm:text-3xl font-bold text-center mb-12 sm:mb-16">Get in Touch</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
            <form 
              id="contact-form"
              className="space-y-6" 
              aria-label="Contact form"
              onSubmit={handleSubmit}
            >
              {['name', 'email'].map((field) => (
                <div key={field}>
                  <label htmlFor={field} className="block text-sm font-medium text-gray-700 mb-2">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </label>
                  <input 
                    type={field === 'email' ? 'email' : 'text'}
                    id={field}
                    name={field}
                    value={formData[field as keyof typeof formData]}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required 
                  />
                </div>
              ))}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4} 
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                ></textarea>
              </div>
              <button 
                type="submit"
                disabled={status === 'submitting'}
                className={`w-full py-3 rounded-lg transition ${
                  status === 'submitting' 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {status === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
              {status === 'success' && (
                <p className="text-green-600 text-center">Message sent successfully!</p>
              )}
              {status === 'error' && (
                <p className="text-red-600 text-center">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
              <div className="space-y-4">
                {[
                  { icon: Phone, text: '+972 54-566-5473', href: 'tel:+972545665473' },
                  { icon: Mail, text: 'contact@israeli.law', href: 'mailto:contact@israeli.law' },
                  { icon: MapPin, text: '101 a Derech Hevron St (Beit Hanatziv), Jerusalem 9348001', href: 'https://maps.google.com/?q=101+a+Derech+Hevron+St,+Jerusalem,+Israel' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <item.icon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                    {item.href ? (
                      <a href={item.href} className="hover:text-blue-600 transition">{item.text}</a>
                    ) : (
                      <span>{item.text}</span>
                    )}
                  </div>
                ))}
                <a 
                  href="https://wa.me/972545665473"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 text-gray-800 hover:text-blue-600 transition group"
                  aria-label="Contact via WhatsApp"
                >
                  <img 
                    src="https://img.icons8.com/?size=100&id=16713&format=png&color=000000"
                    alt="WhatsApp"
                    className="h-5 w-5"
                    width="20"
                    height="20"
                  />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Office Hours</h3>
              <p className="text-gray-600">
                Sunday - Thursday: 9:00 AM - 6:00 PM<br />
                Friday: Closed<br />
                Saturday: Closed
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}